// src/components/ExportMenu.js
// Phase-3C: Reusable export/share dropdown menu component
// Supports PDF, Image, CSV exports and WhatsApp sharing

import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Download, FileImage, FileText, Table, Share2, 
  MessageCircle, Loader2, ChevronDown 
} from 'lucide-react';
import {
  exportToImage,
  exportToPDF,
  exportToCSV,
  downloadBlob,
  shareViaWhatsApp,
  generateFilename,
  canUseWebShare,
} from '../lib/exportUtils';

/**
 * ExportMenu Component
 * 
 * Props:
 * - elementRef: React ref to the DOM element to export
 * - coilNo: string - Coil number for filename
 * - title: string - Title for PDF export
 * - csvData: Array - Data for CSV export (optional)
 * - csvColumns: Array<{key, label}> - Column definitions for CSV
 * - disabled: boolean - Disable all exports
 * - showCSV: boolean - Show CSV export option (default: false)
 * - fromDate: string - Start date for filename
 * - toDate: string - End date for filename
 * - prefix: string - Filename prefix (default: 'Coil')
 */
const ExportMenu = ({
  elementRef,
  coilNo = 'unknown',
  title = '',
  csvData = null,
  csvColumns = [],
  disabled = false,
  showCSV = false,
  fromDate = null,
  toDate = null,
  prefix = 'Coil',
}) => {
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleExport = async (type, shareVia = null) => {
    if (!elementRef?.current && type !== 'csv') {
      toast.error('Nothing to export');
      return;
    }

    if (type === 'csv' && (!csvData || csvData.length === 0)) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    setExportType(type);

    try {
      let result;
      let filename;

      switch (type) {
        case 'pdf':
          filename = generateFilename(prefix, coilNo, 'pdf', fromDate, toDate);
          result = await exportToPDF(elementRef.current, filename, title);
          break;
        case 'image':
          filename = generateFilename(prefix, coilNo, 'png', fromDate, toDate);
          result = await exportToImage(elementRef.current, filename);
          break;
        case 'csv':
          filename = generateFilename(prefix, coilNo, 'csv', fromDate, toDate);
          result = exportToCSV(csvData, csvColumns, filename);
          break;
        default:
          throw new Error(`Unknown export type: ${type}`);
      }

      if (shareVia === 'whatsapp') {
        const shareResult = await shareViaWhatsApp(
          result.blob,
          result.filename,
          `${title || prefix} - ${coilNo}`
        );
        
        if (shareResult.method === 'desktop') {
          toast.success('File downloaded. Attach it in WhatsApp Web.', {
            duration: 5000,
          });
        } else {
          toast.success('Shared successfully');
        }
      } else {
        downloadBlob(result.blob, result.filename);
        toast.success(`${type.toUpperCase()} exported successfully`);
      }
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  // Inline loading icon render
  const renderLoadingIcon = (type) => {
    if (exporting && exportType === type) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    return null;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          disabled={disabled || exporting}
          className="gap-2"
          data-testid="export-menu-trigger"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Download Section */}
        <DropdownMenuItem 
          onClick={() => handleExport('pdf')}
          disabled={exporting}
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          Download PDF
          {renderLoadingIcon('pdf')}
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('image')}
          disabled={exporting}
          className="gap-2"
        >
          <FileImage className="h-4 w-4" />
          Download Image
          {renderLoadingIcon('image')}
        </DropdownMenuItem>
        
        {showCSV && csvData && csvData.length > 0 && (
          <DropdownMenuItem 
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="gap-2"
          >
            <Table className="h-4 w-4" />
            Download CSV
            {renderLoadingIcon('csv')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Share Section */}
        <DropdownMenuItem 
          onClick={() => handleExport('image', 'whatsapp')}
          disabled={exporting}
          className="gap-2"
        >
          <MessageCircle className="h-4 w-4 text-green-600" />
          Share via WhatsApp
          {isMobile && <Share2 className="h-3 w-3 ml-auto text-muted-foreground" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportMenu;
